import React, { useCallback, useEffect, useState } from "react";

import { Typography, Box } from "@material-ui/core";

import {
  CustomButton,
  CustomDateRangePicker,
  CustomExpandCard,
  CustomIconButton,
  CustomPagination,
  CustomPermission,
  KPICard,
  NoDataFound,
  ReskinningAutocomplete,
  ReskinningInputs,
} from "../../../../../Components";

import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

import { formatNumberCompact } from "../../../../../Helper";

import useStyles from "./styles";
import moment from "moment";
import {
  AEDIcon,
  PlusIcon,
  ReceiptIcon,
  SettingsFour,
} from "../../../../../assets/icons";
import { InvoiceSummary } from "../InvoiceSummary";
import { InvoiceDetails } from "../InvoiceDetails";
import useFetchData from "../../../../../Hooks/useFetchData";
import {
  GetInvoicesWithReceiptsByContact,
  lookupItemsGetId,
} from "../../../../../Services";
import { InvoiceSummarySkeleton } from "../SkeletonLoaders";
import { InvoiceDialog } from "../InvoiceDialog";
import { StaticLookupsIds } from "../../../../../assets/json/StaticLookupsIds";
import { ReceiptDialog } from "../ReceiptDialog";
import { AfterSalePermissions } from "../../../../../Permissions";

function PaymentPanel({ contactId, tranActionDetails }) {
  const styles = useStyles();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 25,
  });
  const dateRangeDefault = {
    startDate: null,
    endDate: null,
    key: "selection",
    selectedDateType: 1,
  };
  const [showFiltersSection, setShowFiltersSection] = useState(true);
  const [dateFilter, setDateFilter] = useState(dateRangeDefault);
  const [params, setParams] = useState({
    pageSize: 25,
    pageIndex: 1,
    contactId,
  });
  const [paymentMode, setPaymentModes] = useState([]);
  const [paymentType, setPaymentTypes] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const { data, loading, refetch } = useFetchData(
    GetInvoicesWithReceiptsByContact,
    params
  );
  const getAllPaymentModes = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.PaymentMode,
    });
    if (!(res && res.status && res.status !== 200)) setPaymentModes(res || []);
    else setPaymentModes([]);
  }, []);
  const getAllPaymentTypes = useCallback(async () => {
    const res = await lookupItemsGetId({
      lookupTypeId: StaticLookupsIds.PaymentType,
    });
    if (!(res && res.status && res.status !== 200)) setPaymentTypes(res || []);
    else setPaymentTypes([]);
  }, []);

  useEffect(() => {
    getAllPaymentModes();
    getAllPaymentTypes();
  }, []);

  const [inputValue, setInputValue] = useState(1);
  const { translate } = useTranslate("AfterSalesClientProfileView");

  const {
    theme: { palette },
  } = useSelectedTheme();

  return (
    <Box className={styles.container}>
      <Box className={styles.actionsContainer}>
        <CustomPermission
          permissionsList={Object.values(AfterSalePermissions)}
          permissionsId={AfterSalePermissions.AddInvoice.permissionsId}
        >
          <CustomButton
            variant="outlined"
            color="secondary"
            startIcon={<PlusIcon width={20} height={20} />}
            onClick={() => setInvoiceDialogOpen(true)}
          >
            {translate("add-invoice")}
          </CustomButton>
        </CustomPermission>
        <CustomPermission
          permissionsList={Object.values(AfterSalePermissions)}
          permissionsId={AfterSalePermissions.AddReceipt.permissionsId}
        >
          <CustomButton
            variant="outlined"
            color="secondary"
            startIcon={<ReceiptIcon width={20} height={20} />}
            onClick={() => setReceiptDialogOpen(true)}
          >
            {translate("add-receipt")}
          </CustomButton>
        </CustomPermission>
      </Box>
      <Box className={styles.kpiContainer}>
        <>
          <KPICard loading={loading}>
            <KPICard.Title>
              <Typography
                component="p"
                variant="body1"
                className={styles.title}
              >
                {translate("total-payments")}
              </Typography>
            </KPICard.Title>
            <KPICard.Content>
              <Box
                display="flex"
                flexDirection="row"
                marginTop="14px"
                justifyContent="space-between"
                alignItems="center"
                bgcolor="background.paper"
              >
                <Typography
                  variant="h2"
                  component="h2"
                  className={styles.subTitle}
                >
                  {typeof data?.result?.[0]?.kpi?.totalInvoiceAmount ===
                  "number"
                    ? formatNumberCompact(
                        data?.result?.[0]?.kpi?.totalInvoiceAmount
                      )
                    : "N/A"}
                  {typeof data?.result?.[0]?.kpi?.totalInvoiceAmount ===
                    "number" && (
                    <AEDIcon
                      className={styles.aedIcon}
                      width="20"
                      height="20"
                    />
                  )}
                </Typography>
              </Box>
            </KPICard.Content>
          </KPICard>
          <KPICard loading={loading}>
            <KPICard.Title>
              <Typography
                component="p"
                variant="body1"
                className={styles.title}
              >
                {translate("remaining-amount")}
              </Typography>
            </KPICard.Title>
            <KPICard.Content>
              <Box
                display="flex"
                flexDirection="row"
                marginTop="14px"
                justifyContent="space-between"
                alignItems="center"
                bgcolor="background.paper"
              >
                <Typography
                  variant="h2"
                  component="h2"
                  className={styles.subTitle}
                >
                  {typeof data?.result?.[0]?.kpi?.totalRemaining === "number"
                    ? formatNumberCompact(
                        data?.result?.[0]?.kpi?.totalRemaining
                      )
                    : "N/A"}
                  {typeof data?.result?.[0]?.kpi?.totalRemaining ===
                    "number" && (
                    <AEDIcon
                      className={styles.aedIcon}
                      width="20"
                      height="20"
                    />
                  )}
                </Typography>
              </Box>
            </KPICard.Content>
          </KPICard>
          <KPICard loading={loading}>
            <KPICard.Title>
              <Typography
                component="p"
                variant="body1"
                className={styles.title}
              >
                {translate("paid-amount")}
              </Typography>
            </KPICard.Title>
            <KPICard.Content>
              <Box
                display="flex"
                flexDirection="row"
                marginTop="14px"
                justifyContent="space-between"
                alignItems="center"
                bgcolor="background.paper"
              >
                <Typography
                  variant="h2"
                  component="h2"
                  className={styles.subTitle}
                >
                  {typeof data?.result?.[0]?.kpi?.totalPaid === "number"
                    ? formatNumberCompact(data?.result?.[0]?.kpi?.totalPaid)
                    : "N/A"}
                  {typeof data?.result?.[0]?.kpi?.totalPaid === "number" && (
                    <AEDIcon
                      className={styles.aedIcon}
                      width="20"
                      height="20"
                    />
                  )}
                </Typography>
              </Box>
            </KPICard.Content>
          </KPICard>
        </>
      </Box>
      <Box className={styles.filterWrapper}>
        <Box className={styles.filtersButtons}>
          <CustomIconButton
            variant="outlined"
            size="md"
            boxShadow="none"
            color="secondary"
            onClick={() => setShowFiltersSection((prev) => !prev)}
          >
            <SettingsFour
              width="20"
              height="20"
              fill={palette.button.secondary_fg}
            />
          </CustomIconButton>
        </Box>
        {showFiltersSection && (
          <Box className={styles.filterSection}>
            <Box className={styles.filtersContainer}>
              <ReskinningAutocomplete
                data={paymentType || []}
                autocompleteClasses={styles.inputwrapper}
                withoutSearchButton
                multiple={false}
                className={styles.autoCompleteInput}
                displayLabel={(option) => option?.lookupItemName || ""}
                chipsLabel={(option) => option?.lookupItemName || ""}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                selectedValues={params.paymentType}
                onChange={(_e, value) => {
                  setParams({ ...params, paymentType: value?.lookupItemId });
                }}
                inputPlaceholder={translate("payment-type")}
                enableTextTruncate={true}
              />
              <ReskinningInputs
                inputPlaceholder={translate("unit-reference-number")}
                fieldClasses={styles.autoCompleteInput}
                onInputChanged={(e) => {
                  if (e.target.value === "") {
                    const ref = { ...params };
                    delete ref.unitRefNo;
                    setParams(ref);
                    return;
                  }
                  setParams({
                    ...params,
                    unitRefNo: e.target.value,
                  });
                }}
              />
              <ReskinningAutocomplete
                data={[
                  { id: "paid", invoiceStatus: "Paid" },
                  { id: "unpaid", invoiceStatus: "Unpaid" },
                  { id: "canceled", invoiceStatus: "Canceled" },
                  { id: "partially paid", invoiceStatus: "Partially paid" },
                ]}
                autocompleteClasses={styles.inputwrapper}
                withoutSearchButton
                multiple={false}
                className={styles.autoCompleteInput}
                displayLabel={(option) => option?.invoiceStatus || ""}
                chipsLabel={(option) => option?.invoiceStatus || ""}
                getOptionSelected={(option, value) => option?.id === value?.id}
                selectedValues={params.invoiceStatus}
                onChange={(_e, value) => {
                  setParams({ ...params, invoiceStatus: value?.invoiceStatus });
                }}
                inputPlaceholder={translate("status")}
              />
              <ReskinningAutocomplete
                data={paymentMode || []}
                inputPlaceholder={translate("payment-mode")}
                autocompleteClasses={styles.inputwrapper}
                withoutSearchButton
                multiple={false}
                className={styles.autoCompleteInput}
                displayLabel={(option) => option?.lookupItemName || ""}
                chipsLabel={(option) => option?.lookupItemName || ""}
                selectedValues={params.paymentFor}
                getOptionSelected={(option, value) =>
                  option?.lookupItemId === value?.lookupItemId
                }
                onChange={(_e, value) => {
                  setParams({ ...params, paymentFor: value?.lookupItemId });
                }}
              />

              <ReskinningInputs
                inputPlaceholder={translate("property-name-nvoice-number")}
                overInputIcon
                fieldClasses={styles.searchInput}
                onInputChanged={(e) => {
                  if (e.target.value === "") {
                    const ref = { ...params };
                    delete ref.propertyName;
                    setParams({
                      ...ref,
                    });
                    return;
                  }
                  setParams({
                    ...params,
                    propertyName: e.target.value,
                  });
                }}
              />
              <ReskinningInputs
                inputPlaceholder={translate("invoice-number")}
                overInputIcon
                fieldClasses={styles.searchInput}
                onInputChanged={(e) => {
                  if (e.target.value === "") {
                    const ref = { ...params };
                    delete ref.invoiceId;
                    setParams({
                      ...ref,
                    });
                    return;
                  }
                  setParams({
                    ...params,
                    invoiceId: e.target.value,
                  });
                }}
              />
            </Box>
            <Box className={styles.dateFilterContainer}>
              <Box className={styles.dateFilter}>{translate("due-date")}</Box>
              <CustomDateRangePicker
                onClearClicked={() => {
                  setDateFilter(dateRangeDefault);
                  setParams({
                    ...params,
                    dueDateFrom: "",
                    dueDateTo: "",
                  });
                }}
                ranges={[dateFilter]}
                onDateChanged={(selectedDate) => {
                  setDateFilter((item) => ({
                    ...item,
                    startDate:
                      selectedDate.selection &&
                      selectedDate.selection.startDate,
                    endDate: !selectedDate.selection?.endDate
                      ? null
                      : new Date(
                          moment(
                            selectedDate.selection &&
                              selectedDate.selection.endDate
                          ).endOf("day")
                        ),
                    key: "selection",
                  }));
                }}
                emptyLabel="dd/mm/yyyy"
                displayFormat="MMM D, YYYY"
                onSave={(ranges) => {
                  setParams({
                    ...params,
                    dueDateFrom: ranges.startDate,
                    dueDateTo: ranges.endDate,
                  });
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Box className={styles.mainContainer}>
        {loading &&
          Array.from({ length: 5 }).map((_, index) => (
            <InvoiceSummarySkeleton key={index} />
          ))}
        {!loading &&
          data?.result?.length > 0 &&
          data?.result.map((invoice) => (
            <CustomExpandCard
              key={invoice.invoiceId}
              expandedRootClassName={styles.expandedRoot}
              // summaryRootClassName={styles.summaryRoot}
              CardSummary={<InvoiceSummary invoice={invoice} />}
              CardExpandContent={() => (
                <InvoiceDetails receipts={invoice?.receipts} />
              )}
            />
          ))}
        {!loading && data?.result?.length === 0 && (
          <NoDataFound title={translate("no-invoices")} subTitle="" />
        )}
        {!loading && data?.result?.length > 0 && (
          <CustomPagination
            totalItems={data.totalCount}
            itemsPerPage={pagination.itemsPerPage}
            currentPage={pagination.currentPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            isLoading={loading}
            onPageChange={(page) =>
              setPagination((prev) => ({ ...prev, currentPage: page }))
            }
            onItemsPerPageChange={(items) =>
              setPagination((prev) => ({ ...prev, itemsPerPage: items }))
            }
          />
        )}
      </Box>
      <InvoiceDialog
        tranActionDetails={tranActionDetails}
        onClose={() => setInvoiceDialogOpen(false)}
        open={invoiceDialogOpen}
        refetch={refetch}
      />
      <ReceiptDialog
        tranActionDetails={tranActionDetails}
        onClose={() => setReceiptDialogOpen(false)}
        open={receiptDialogOpen}
        invoices={data?.result || []}
        refetch={refetch}
      />
    </Box>
  );
}

export default PaymentPanel;
